import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { useAppDownload } from '@/context/AppDownloadContext';
import {
  favoriteTypeConfig,
  favoriteStats,
  favoriteRadios,
  favoritePrograms,
  favoriteLocutores,
  favoriteEventos,
  favoriteConcursos,
  favoriteClubs,
  allFavorites,
  quickAccessFavorites,
  favoriteRules,
  getFavoritesByType,
  getOnAirFavorites,
  type FavoriteType,
  type FavoriteItem,
  type FavoriteRadio,
  type FavoritePrograma,
  type FavoriteLocutor,
  type FavoriteEvento,
  type FavoriteConcurso,
  type FavoriteClub,
} from '@/mocks/rfwFavorites';

type TabKey = FavoriteType;

function TypeBadge({ type }: { type: FavoriteType }) {
  const c = favoriteTypeConfig[type];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

export default function Favoritos() {
  const { openModal: openDownloadModal } = useAppDownload();
  const [activeTab, setActiveTab] = useState<TabKey>('radio');

  // Local state for managing favorites (unfavorite)
  const [unfavoritedIds, setUnfavoritedIds] = useState<Set<string>>(new Set());
  const [confirmUnfavorite, setConfirmUnfavorite] = useState<{ id: string; name: string; type: FavoriteType } | null>(null);

  const handleUnfavorite = () => {
    if (!confirmUnfavorite) return;
    setUnfavoritedIds((prev) => new Set(prev).add(confirmUnfavorite.id));
    setConfirmUnfavorite(null);
  };

  const currentFavorites = useMemo(() => {
    const all = getFavoritesByType(activeTab);
    return all.filter((f) => !unfavoritedIds.has(f.id));
  }, [activeTab, unfavoritedIds]);

  const onAirItems = useMemo(() => getOnAirFavorites().filter((f) => !unfavoritedIds.has(f.id)), [unfavoritedIds]);

  const totalActive = useMemo(
    () => allFavorites.filter((f) => !unfavoritedIds.has(f.id)).length,
    [unfavoritedIds],
  );

  const countsByType = useMemo(() => {
    const counts: Record<FavoriteType, number> = {
      radio: 0, programa: 0, locutor: 0, evento: 0, concurso: 0, club: 0,
    };
    allFavorites.forEach((f) => {
      if (!unfavoritedIds.has(f.id)) counts[f.favoriteType]++;
    });
    return counts;
  }, [unfavoritedIds]);

  const tabConfig: { key: TabKey; icon: string; label: string; color: string }[] = Object.entries(favoriteTypeConfig).map(([key, c]) => ({
    key: key as TabKey,
    icon: c.icon,
    label: c.labelPlural,
    color: c.color,
  }));

  const formatNumber = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`);

  return (
    <div className="min-h-screen bg-background-100/50 flex flex-col">
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-10">

          {/* Hero */}
          <div className="bg-background-50 rounded-2xl overflow-hidden">
            <div className="relative h-40 md:h-48 bg-gradient-to-br from-rose-500/20 via-pink-400/15 to-amber-400/10 overflow-hidden">
              <div className="absolute inset-0 bg-background-50/25"></div>
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-rose-500/10 blur-3xl"></div>
              <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-amber-400/10 blur-3xl"></div>

              <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center mb-3">
                  <i className="ri-heart-3-line text-background-50 text-2xl"></i>
                </div>
                <h2 className="text-xl md:text-2xl font-black text-foreground-950 mb-1">Mis Favoritos</h2>
                <p className="text-sm text-foreground-500">{totalActive} guardados · {onAirItems.length} al aire ahora</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 md:grid-cols-6 divide-x divide-background-200">
              {tabConfig.map((tab) => (
                <div key={tab.key} className="px-3 py-4 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <i className={`${tab.icon} ${tab.color} text-[10px]`}></i>
                    <span className="text-[9px] text-foreground-500">{tab.label}</span>
                  </div>
                  <div className="text-sm font-bold text-foreground-950">{countsByType[tab.key]}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Access Bar */}
          <div className="mt-4 bg-background-50 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-flashlight-line text-amber-500 text-sm"></i>
              <span className="text-xs font-bold text-foreground-950">Acceso Rápido</span>
              <span className="text-[10px] text-foreground-400">— tus top 8 a un clic</span>
            </div>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
              {quickAccessFavorites.filter((f) => !unfavoritedIds.has(f.id)).slice(0, 8).map((item) => {
                const tc = favoriteTypeConfig[item.favoriteType];
                const imgUrl = item.favoriteType === 'locutor'
                  ? (item as FavoriteLocutor).photo
                  : 'image' in item ? (item as any).image : '';
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.favoriteType)}
                    className="flex flex-col items-center gap-1 group cursor-pointer"
                  >
                    <div className="relative">
                      <img src={imgUrl} alt={'name' in item ? item.name : ''} className="w-10 h-10 rounded-xl object-cover group-hover:scale-105 transition-transform" />
                      <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ${tc.bgColor} flex items-center justify-center border-2 border-background-50`}>
                        <i className={`${tc.icon} text-[7px] ${tc.color}`}></i>
                      </div>
                    </div>
                    <span className="text-[9px] text-foreground-600 line-clamp-1 max-w-[64px] text-center">{'name' in item ? item.name : ''}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* On Air Strip */}
          {onAirItems.length > 0 && (
            <div className="mt-4 flex items-center gap-3 px-4 py-3 rounded-2xl bg-emerald-50 border border-emerald-200/60 overflow-x-auto">
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span className="text-xs font-bold text-emerald-700 whitespace-nowrap">{onAirItems.length} al aire</span>
              </div>
              <div className="h-4 w-px bg-emerald-300 shrink-0"></div>
              <div className="flex items-center gap-2">
                {onAirItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.favoriteType)}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 hover:bg-emerald-200 text-[10px] font-medium text-emerald-800 whitespace-nowrap cursor-pointer transition-colors"
                  >
                    <i className={`${favoriteTypeConfig[item.favoriteType].icon} text-[9px]`}></i>
                    {'name' in item ? item.name : ''}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="mt-6 flex items-center gap-1 bg-background-50 rounded-full p-1 overflow-x-auto border border-background-200/50">
            {tabConfig.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.key ? 'bg-white/20 text-white' : 'bg-background-100 text-foreground-400'
                }`}>
                  {countsByType[tab.key]}
                </span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="mt-6">
            {currentFavorites.length === 0 ? (
              <EmptyState
                type={activeTab}
                onDiscover={() => {
                  if (activeTab === 'concurso') setActiveTab('evento');
                  else if (activeTab === 'club') setActiveTab('radio');
                }}
              />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentFavorites.map((item) => (
                  <FavoriteItemCard
                    key={item.id}
                    item={item}
                    onUnfavorite={() => setConfirmUnfavorite({ id: item.id, name: 'name' in item ? item.name : '', type: item.favoriteType })}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Discover */}
          <div className="mt-8 bg-background-50 rounded-2xl p-6">
            <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-5">
              <i className="ri-compass-line text-accent-500"></i>
              Descubrí más
            </h3>
            <p className="text-xs text-foreground-500 leading-relaxed mb-4">
              Explorá la red de RFW para encontrar nuevas radios, programas, locutores, eventos, concursos y clubes. Cuanto más agregás a favoritos, más personalizada se vuelve tu experiencia.
            </p>
            <button
              onClick={openDownloadModal}
              className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-2-line mr-1.5"></i>
              Descargar App RFW
            </button>
          </div>

          {/* Reglas y tipos */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {favoriteRules.map((rule) => (
              <div key={rule.id} className={`p-4 rounded-xl ${rule.bgColor} border border-background-200/50 text-center`}>
                <div className="w-8 h-8 rounded-full bg-background-50 flex items-center justify-center mx-auto mb-2">
                  <i className={`${rule.icon} ${rule.color} text-sm`}></i>
                </div>
                <p className="text-xs font-bold text-foreground-950 mb-1">{rule.title}</p>
                <p className="text-[10px] text-foreground-500">{rule.description}</p>
              </div>
            ))}
          </div>

          {/* Cross-links */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/oyente"
              className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-50 transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center">
                  <i className="ri-user-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Tu Perfil</h4>
                  <p className="text-xs text-foreground-500">Estadísticas y actividad</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link
              to="/insignias"
              className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-50 transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                  <i className="ri-award-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Insignias</h4>
                  <p className="text-xs text-foreground-500">Colección de logros</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link
              to="/guardados"
              className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-50 transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-500 flex items-center justify-center">
                  <i className="ri-bookmark-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Guardados</h4>
                  <p className="text-xs text-foreground-500">Contenido que guardaste</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
          </div>

          {/* Regla final */}
          <div className="mt-8 p-6 rounded-2xl bg-rose-100/70 border border-rose-200/50 text-center">
            <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
              <i className="ri-cursor-line text-lg text-white"></i>
            </div>
            <p className="text-sm font-bold text-foreground-950 mb-1">
              Máximo 1 clic para acceder.
            </p>
            <p className="text-xs text-foreground-600 max-w-lg mx-auto">
              En RFW, tus favoritos están siempre a un clic. Radios, programas, locutores, eventos, concursos y clubes — todo lo que marcaste como favorito vive acá, ordenado y listo para que llegues sin demoras.
            </p>
          </div>

        </div>
      </main>

      <Footer />

      {/* Unfavorite Confirmation Modal */}
      {confirmUnfavorite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmUnfavorite(null)}></div>
          <div className="relative bg-background-50 rounded-2xl p-6 max-w-sm w-full shadow-lg">
            <div className="text-center mb-5">
              <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-rose-100 flex items-center justify-center">
                <i className="ri-close-circle-line text-rose-500 text-xl"></i>
              </div>
              <h4 className="text-sm font-bold text-foreground-950 mb-1">¿Quitar de favoritos?</h4>
              <p className="text-xs text-foreground-500">
                Vas a quitar <strong>{confirmUnfavorite.name}</strong> de tus favoritos.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmUnfavorite(null)}
                className="flex-1 px-4 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleUnfavorite}
                className="flex-1 px-4 py-2.5 bg-rose-500 hover:bg-rose-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Quitar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="md:hidden h-20"></div>
    </div>
  );
}

/* ══════════ Favorite Item Card (unified for all 6 types) ══════════ */
function FavoriteItemCard({
  item,
  onUnfavorite,
}: {
  item: FavoriteItem;
  onUnfavorite: () => void;
}) {
  const tc = favoriteTypeConfig[item.favoriteType];

  switch (item.favoriteType) {
    case 'radio':
      return <RadioCard radio={item as FavoriteRadio} onUnfavorite={onUnfavorite} />;
    case 'programa':
      return <ProgramCard program={item as FavoritePrograma} onUnfavorite={onUnfavorite} />;
    case 'locutor':
      return <LocutorCard locutor={item as FavoriteLocutor} onUnfavorite={onUnfavorite} />;
    case 'evento':
      return <EventoCard evento={item as FavoriteEvento} onUnfavorite={onUnfavorite} />;
    case 'concurso':
      return <ConcursoCard concurso={item as FavoriteConcurso} onUnfavorite={onUnfavorite} />;
    case 'club':
      return <ClubCard club={item as FavoriteClub} onUnfavorite={onUnfavorite} />;
    default:
      return null;
  }
}

/* ══════════ Radio Card ══════════ */
function RadioCard({ radio, onUnfavorite }: { radio: FavoriteRadio; onUnfavorite: () => void }) {
  return (
    <div className="bg-background-50 rounded-2xl overflow-hidden group">
      <div className="relative h-36">
        <img src={radio.image} alt={radio.name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
        {radio.isLive && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse"></span>
            <span className="text-[10px] font-bold text-background-50">EN VIVO</span>
          </div>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onUnfavorite(); }}
          className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-background-50/80 hover:bg-rose-500 text-foreground-500 hover:text-background-50 flex items-center justify-center cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
          title="Quitar de favoritos"
        >
          <i className="ri-heart-fill text-sm"></i>
        </button>
      </div>
      <div className="p-4">
        <h4 className="text-sm font-bold text-foreground-950 mb-1">{radio.name}</h4>
        <div className="flex items-center gap-2 text-xs text-foreground-500 mb-1">
          <span>{radio.genre}</span>
          <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
          <span>{radio.location}</span>
        </div>
        <div className="flex items-center gap-1.5 mb-3">
          <TypeBadge type="radio" />
          <span className="text-[9px] text-foreground-400">{formatCompact(radio.followers)} seguidores</span>
        </div>
        {radio.currentShow && (
          <div className="pt-3 border-t border-background-100">
            <div className="text-[10px] text-foreground-400 mb-0.5">Al aire ahora</div>
            <div className="text-xs font-semibold text-foreground-700">{radio.currentShow}</div>
          </div>
        )}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-background-100">
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-heart-3-line text-[8px] text-rose-400"></i>
            {radio.favoritedAtRelative}
          </span>
          <span className="text-[10px] font-medium text-foreground-500">{radio.frequency}</span>
        </div>
      </div>
    </div>
  );
}

/* ══════════ Program Card ══════════ */
function ProgramCard({ program, onUnfavorite }: { program: FavoritePrograma; onUnfavorite: () => void }) {
  const showDays = program.days.length > 4
    ? `${program.days.slice(0, 3).join(', ')}...`
    : program.days.join(', ');

  return (
    <div className="bg-background-50 rounded-2xl overflow-hidden group">
      <div className="relative h-36">
        <img src={program.image} alt={program.name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
        {program.onAir && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse"></span>
            <span className="text-[10px] font-bold text-background-50">AL AIRE</span>
          </div>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onUnfavorite(); }}
          className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-background-50/80 hover:bg-rose-500 text-foreground-500 hover:text-background-50 flex items-center justify-center cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
        >
          <i className="ri-heart-fill text-sm"></i>
        </button>
      </div>
      <div className="p-4">
        <h4 className="text-sm font-bold text-foreground-950 mb-1">{program.name}</h4>
        <p className="text-xs text-foreground-500 mb-1">{program.station}</p>
        <p className="text-xs text-foreground-400 mb-3">{program.host}</p>
        <div className="flex items-center gap-1.5 mb-3">
          <i className="ri-time-line text-foreground-400 text-xs"></i>
          <span className="text-xs font-medium text-foreground-700">{program.schedule}</span>
        </div>
        <div className="flex flex-wrap gap-1 mb-3">
          {program.days.map((day) => (
            <span key={day} className="px-2 py-0.5 bg-background-100 text-foreground-600 text-[10px] font-medium rounded-md">
              {day.slice(0, 3)}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-background-100">
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-heart-3-line text-[8px] text-rose-400"></i>
            {program.favoritedAtRelative}
          </span>
          <span className="text-[10px] text-foreground-500">{program.nextEpisode}</span>
        </div>
      </div>
    </div>
  );
}

/* ══════════ Locutor Card ══════════ */
function LocutorCard({ locutor, onUnfavorite }: { locutor: FavoriteLocutor; onUnfavorite: () => void }) {
  return (
    <div className="bg-background-50 rounded-2xl overflow-hidden group">
      <div className="relative h-36">
        <img src={locutor.photo} alt={locutor.name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
        {locutor.onAir && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse"></span>
            <span className="text-[10px] font-bold text-background-50">AL AIRE</span>
          </div>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onUnfavorite(); }}
          className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-background-50/80 hover:bg-rose-500 text-foreground-500 hover:text-background-50 flex items-center justify-center cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
        >
          <i className="ri-heart-fill text-sm"></i>
        </button>
      </div>
      <div className="p-4">
        <h4 className="text-sm font-bold text-foreground-950 mb-1">{locutor.name}</h4>
        <p className="text-xs text-foreground-500 mb-0.5">{locutor.role}</p>
        <p className="text-xs text-foreground-400 mb-3">{locutor.station}</p>
        <div className="flex items-center gap-2 mb-3">
          <span className="px-2 py-0.5 bg-background-100 text-foreground-600 text-[10px] font-medium rounded-md">{locutor.genre}</span>
          <span className="text-[10px] text-foreground-400">{formatCompact(locutor.followers)} seguidores</span>
        </div>
        {locutor.shows.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {locutor.shows.map((show) => (
              <span key={show} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-[10px] font-medium rounded-md">{show}</span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between pt-3 border-t border-background-100">
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-heart-3-line text-[8px] text-rose-400"></i>
            {locutor.favoritedAtRelative}
          </span>
          <span className={`text-[10px] font-medium ${locutor.onAir ? 'text-emerald-600' : 'text-foreground-400'}`}>
            {locutor.onAir ? 'Al aire' : 'Offline'}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ══════════ Evento Card ══════════ */
function EventoCard({ evento, onUnfavorite }: { evento: FavoriteEvento; onUnfavorite: () => void }) {
  return (
    <div className="bg-background-50 rounded-2xl overflow-hidden group">
      <div className="relative h-36">
        <img src={evento.image} alt={evento.name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/90 rounded-full">
          <i className="ri-calendar-event-line text-background-50 text-[9px]"></i>
          <span className="text-[10px] font-bold text-background-50">{evento.eventType}</span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onUnfavorite(); }}
          className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-background-50/80 hover:bg-rose-500 text-foreground-500 hover:text-background-50 flex items-center justify-center cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
        >
          <i className="ri-heart-fill text-sm"></i>
        </button>
      </div>
      <div className="p-4">
        <h4 className="text-sm font-bold text-foreground-950 mb-1">{evento.name}</h4>
        <div className="flex items-center gap-1.5 mb-1 text-xs text-foreground-500">
          <i className="ri-map-pin-line text-[10px]"></i>
          <span>{evento.location}</span>
        </div>
        <div className="flex items-center gap-1.5 mb-3 text-xs text-foreground-500">
          <i className="ri-calendar-line text-[10px]"></i>
          <span>{evento.dateRelative}</span>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <TypeBadge type="evento" />
          <span className="text-[10px] text-foreground-400">{formatCompact(evento.attendees)} asistentes</span>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-background-100">
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-heart-3-line text-[8px] text-rose-400"></i>
            {evento.favoritedAtRelative}
          </span>
          <span className="text-[10px] text-foreground-500">{evento.organizer}</span>
        </div>
      </div>
    </div>
  );
}

/* ══════════ Concurso Card ══════════ */
function ConcursoCard({ concurso, onUnfavorite }: { concurso: FavoriteConcurso; onUnfavorite: () => void }) {
  const statusBadge = {
    activo: 'bg-emerald-100 text-emerald-700',
    finalizado: 'bg-foreground-100 text-foreground-500',
    por_iniciar: 'bg-amber-100 text-amber-700',
  };
  const statusLabel = {
    activo: 'Activo',
    finalizado: 'Finalizado',
    por_iniciar: 'Próximo',
  };

  return (
    <div className="bg-background-50 rounded-2xl overflow-hidden group">
      <div className="relative h-36">
        <img src={concurso.image} alt={concurso.name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${statusBadge[concurso.status]}`}>
            {statusLabel[concurso.status]}
          </span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onUnfavorite(); }}
          className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-background-50/80 hover:bg-rose-500 text-foreground-500 hover:text-background-50 flex items-center justify-center cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
        >
          <i className="ri-heart-fill text-sm"></i>
        </button>
      </div>
      <div className="p-4">
        <h4 className="text-sm font-bold text-foreground-950 mb-1">{concurso.name}</h4>
        <p className="text-xs text-foreground-500 mb-1">{concurso.contestType}</p>
        <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold text-sky-600">
          <i className="ri-trophy-line text-[10px]"></i>
          <span>{concurso.prize}</span>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <TypeBadge type="concurso" />
          <span className="text-[10px] text-foreground-400">{formatCompact(concurso.participants)} participantes</span>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-background-100">
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-heart-3-line text-[8px] text-rose-400"></i>
            {concurso.favoritedAtRelative}
          </span>
          <span className="text-[10px] font-medium text-foreground-500">Cierra: {concurso.endDateRelative}</span>
        </div>
      </div>
    </div>
  );
}

/* ══════════ Club Card ══════════ */
function ClubCard({ club, onUnfavorite }: { club: FavoriteClub; onUnfavorite: () => void }) {
  return (
    <div className="bg-background-50 rounded-2xl overflow-hidden group">
      <div className="relative h-36">
        <img src={club.image} alt={club.name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-orange-500 rounded-full">
          <i className="ri-vip-crown-line text-background-50 text-[9px]"></i>
          <span className="text-[10px] font-bold text-background-50">{club.clubType}</span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onUnfavorite(); }}
          className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-background-50/80 hover:bg-rose-500 text-foreground-500 hover:text-background-50 flex items-center justify-center cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
        >
          <i className="ri-heart-fill text-sm"></i>
        </button>
      </div>
      <div className="p-4">
        <h4 className="text-sm font-bold text-foreground-950 mb-1">{club.name}</h4>
        <p className="text-xs text-foreground-500 mb-3">{club.category}</p>
        <div className="flex items-center gap-2 mb-3">
          <TypeBadge type="club" />
          <span className="text-[10px] text-foreground-400">{formatCompact(club.members)} miembros</span>
        </div>
        {club.recentActivity && (
          <div className="mb-3 p-2 rounded-lg bg-orange-50 border border-orange-100">
            <p className="text-[10px] text-orange-700 line-clamp-2">{club.recentActivity}</p>
          </div>
        )}
        <div className="flex items-center justify-between pt-3 border-t border-background-100">
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-heart-3-line text-[8px] text-rose-400"></i>
            {club.favoritedAtRelative}
          </span>
          <span className={`text-[10px] font-medium ${club.isActive ? 'text-emerald-600' : 'text-foreground-400'}`}>
            {club.isActive ? 'Activo' : 'Inactivo'}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ══════════ Empty State ══════════ */
function EmptyState({ type, onDiscover }: { type: FavoriteType; onDiscover: () => void }) {
  const tc = favoriteTypeConfig[type];
  return (
    <div className="bg-background-50 rounded-2xl p-10 text-center">
      <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center ${tc.bgColor}`}>
        <i className={`${tc.icon} text-xl ${tc.color}`}></i>
      </div>
      <h4 className="text-sm font-bold text-foreground-700 mb-1">No tenés {tc.labelPlural.toLowerCase()} en favoritos</h4>
      <p className="text-xs text-foreground-500 max-w-xs mx-auto mb-4">{tc.description}</p>
      <button
        onClick={onDiscover}
        className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
      >
        <i className="ri-compass-line mr-1.5"></i>
        Explorar
      </button>
    </div>
  );
}

function formatCompact(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
}